import {
  Copy,
  ExternalLink,
  Import,
  Maximize2,
  Play,
  Square,
  Upload,
  type LucideIcon,
} from "lucide-react";

import { closeAllDataTableDropdownMenus } from "@/components/ui/DropdownMenu";
import { BasicTooltip } from "@/components/ui/Tooltip";

interface CellActionsProps {
  onOpen?: () => void;
  onCopy?: () => void;
  onImport?: () => void;
  importLabel?: string;
  onRun?: () => void;
  onStop?: () => void;
  onUpload?: () => void;
  openRequestUrl?: string;
  openRequestLabel?: string;
  isVisible: boolean;
  className?: string;
}

export default function CellActions({
  onOpen,
  onCopy,
  onImport,
  importLabel,
  onRun,
  onStop,
  onUpload,
  openRequestUrl,
  openRequestLabel,
  isVisible,
  className,
}: CellActionsProps) {
  let runOrStop: { label: string; icon: LucideIcon; onClick: () => void } | null = null;
  if (onStop) {
    runOrStop = { label: "Stop cell", icon: Square, onClick: onStop };
  } else if (onRun) {
    runOrStop = { label: "Run cell", icon: Play, onClick: onRun };
  }

  if (!onOpen && !onCopy && !onImport && !runOrStop && !openRequestUrl && !onUpload) {
    return null;
  }

  const RunOrStopIcon = runOrStop?.icon;

  return (
    <div
      className={`absolute right-1 top-1/2 z-[100] flex -translate-y-1/2 items-center gap-1 rounded-sm bg-background/90 p-0.5 text-muted-foreground shadow-sm backdrop-blur-sm transition-opacity duration-150 ${
        isVisible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
      } ${className ?? ""}`}
    >
      {onImport ? (
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted hover:text-foreground"
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onImport();
          }}
          onMouseDown={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
          aria-label={importLabel ?? "Import into cell"}
          title={importLabel ?? "Import into cell"}
          tabIndex={-1}
        >
          <Import className="h-3 w-3" />
        </button>
      ) : null}

      {onUpload ? (
        <BasicTooltip content="Import a file">
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted hover:text-foreground"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onUpload();
            }}
            onMouseDown={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
            aria-label="Import a file"
            tabIndex={-1}
          >
            <Upload className="h-3 w-3" />
          </button>
        </BasicTooltip>
      ) : null}

      {openRequestUrl ? (
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted hover:text-foreground"
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            window.open(openRequestUrl, "_blank", "noopener,noreferrer");
          }}
          onMouseDown={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
          aria-label={openRequestLabel ?? "Open linked execution"}
          title={openRequestLabel ?? "Open linked execution"}
          tabIndex={-1}
        >
          <ExternalLink className="h-3 w-3" />
        </button>
      ) : null}

      {onOpen ? (
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted hover:text-foreground"
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            closeAllDataTableDropdownMenus();
            onOpen();
          }}
          onMouseDown={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
          tabIndex={-1}
        >
          <Maximize2 className="h-3 w-3" />
        </button>
      ) : null}

      {onCopy ? (
        <BasicTooltip content="Copy cell">
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted hover:text-foreground"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              void onCopy();
            }}
            onMouseDown={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
            aria-label="Copy cell"
            tabIndex={-1}
          >
            <Copy className="h-3 w-3" />
          </button>
        </BasicTooltip>
      ) : null}

      {runOrStop && RunOrStopIcon ? (
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted hover:text-foreground"
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            runOrStop.onClick();
          }}
          onMouseDown={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
          aria-label={runOrStop.label}
          title={runOrStop.label}
          tabIndex={-1}
        >
          <RunOrStopIcon className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
}
